// Import Libraries
import React from 'react';
import PropTypes from 'prop-types';
import { builderDispatch } from '../../_utils/ui';
import translate from '../../_utils/tools/translate';
import {
  isPostalCodeLocal,
  shouldStripPostalCodeWhitespace,
} from '../../_utils/validations';
import DisplayPrice from '../../_utils/display-price';
import CartItemsChangeEvents from '../cart-items-change-events';

import DeliveryOptionsForm from '../../_utils/_delivery-options/delivery-options-form';
import Pluralizer from '.././pluralizer';
import TaxesAndDeliveryPrompt from '../taxes-and-delivery-prompt';
import CheckoutButtons from './checkout-buttons';
import { checkoutBlockers } from '../checkout-blockers';
import ZipcodePopover from '../zipcode-popover';
import InvalidZipCodeWarning from '../../_common/postal-code-input/invalid-postal-code-warning';
import UpsellAmount from '../upsell-amount';

class Full extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      popoverOpen: false,
      zipCodeValid: false,
    };

    this.zipCodeField = undefined;
    this.setZipCodeField = ref => {
      this.zipCodeField = ref;
    };
  }

  componentDidMount() {
    const { zipCode } = this.context;

    this.setState({
      popoverOpen: !zipCode,
      zipCodeValid: isPostalCodeLocal(zipCode),
    });
  }

  subTotal = () => {
    const { price, shippingCost } = this.props;
    return price + shippingCost;
  };

  showPopover = e => {
    if (e) e.preventDefault();

    const { zipCode } = this.context;

    this.setState({
      popoverOpen: true,
      zipCodeValid: isPostalCodeLocal(zipCode),
    });
  };

  hidePopover = () => {
    const { zipCode } = this.context;

    this.setState({
      popoverOpen: false,
      zipCodeValid: isPostalCodeLocal(zipCode),
    });
  };

  handleZipCodeChange = e => {
    const zipCode = e.target.value.replace(/ /g, '');
    this.setState({
      zipCodeValid: isPostalCodeLocal(zipCode),
    });
  };

  handleZipCodeKeyUp = e => {
    if (e.keyCode === 13) {
      e.preventDefault();
      this.handleZipCodeUpdateClick();
    }
  };

  handleZipCodeUpdateClick = () => {
    let zipCode = this.zipCodeField.value;

    if (shouldStripPostalCodeWhitespace()) {
      zipCode = zipCode.replace(/ /g, '');
    }

    const valid = isPostalCodeLocal(zipCode);

    this.setState(
      {
        zipCodeValid: valid,
      },
      () => {
        if (!valid) return;

        builderDispatch.dispatch({
          type: CartItemsChangeEvents.ZIP_CODE_CHANGE,
          value: zipCode,
        });
      }
    );
  };

  handleZipCodeCancelClick = () => {
    const { zipCode } = this.context;
    this.zipCodeField.value = zipCode;
    this.hidePopover();
  };

  renderDeliveryOptionsForm = () => {
    const { deliveryOptions, selectedDeliveryOption } = this.props;

    return (
      <div data-test-id="delivery-options-form">
        <p className="lead total-name">Delivery Option</p>
        <DeliveryOptionsForm
          hideIcon
          options={deliveryOptions}
          selectedOption={selectedDeliveryOption}
        />
      </div>
    );
  };

  render() {
    const { zipCode, uploadedToAlbumPath } = this.context;
    const {
      quantity,
      checkoutBlocker,
      deliveryOptions,
      freeShippingEnabled,
      freeShippingAssetUrl,
      freeShippingThreshold,
      homeDeliveryItemsPrice,
      crossSellItemCheckOutClick,
    } = this.props;
    const { popoverOpen, zipCodeValid } = this.state;

    const checkoutDisabled =
      checkoutBlocker !== checkoutBlockers.none || popoverOpen || !zipCodeValid;

    return (
      <>
        <p data-test-id="item-count">
          <Pluralizer count={quantity} singular="item" />
        </p>
        <p>
          {zipCode ? `Ship to ${zipCode}` : `No ${translate('ZIP code')} given`}
          <small className="ml-1">
            <a id="popover-anchor" href="#" onClick={this.showPopover}>
              Change
            </a>
          </small>
        </p>
        {!zipCodeValid && <InvalidZipCodeWarning />}
        {deliveryOptions && this.renderDeliveryOptionsForm()}
        <p>
          <TaxesAndDeliveryPrompt
            us="Check out to see shipping, taxes & fees"
            row="Please check out to see delivery costs"
          />
        </p>
        <hr />
        <p
          className="d-flex justify-content-between lead m-0"
          data-test-id="subtotal">
          <span className="total-name">Subtotal</span>
          <span className="price" data-test-id="price">
            <DisplayPrice price={this.subTotal()} />
          </span>
        </p>
        {freeShippingEnabled && (
          <UpsellAmount
            price={homeDeliveryItemsPrice}
            freeShippingThreshold={freeShippingThreshold}
            assetUrl={freeShippingAssetUrl}
          />
        )}
        <CheckoutButtons
          quantity={quantity}
          checkoutBlocker={checkoutBlocker}
          crossSellItemCheckOutClick={crossSellItemCheckOutClick}
          checkoutDisabled={checkoutDisabled}
          handleCheckoutClick={this.handleCheckoutClick}
        />
        {uploadedToAlbumPath && (
          <p className="uploaded-to-album" data-test-id="uploaded-to-album">
            For your convenience, all photos uploaded are also saved to &lsquo;
            <a href={uploadedToAlbumPath} data-test-id="uploaded-to-album-link">
              My Photos
            </a>
            &rsquo;
          </p>
        )}
        <ZipcodePopover
          onRef={ref => this.setZipCodeField(ref)}
          popoverOpen={popoverOpen}
          zipCodeValid={zipCodeValid}
          zipCode={zipCode}
          handleZipCodeChange={this.handleZipCodeChange}
          handleZipCodeKeyUp={this.handleZipCodeKeyUp}
          handleZipCodeCancelClick={this.handleZipCodeCancelClick}
          handleZipCodeUpdateClick={this.handleZipCodeUpdateClick}
        />
      </>
    );
  }
}

Full.propTypes = {
  checkoutBlocker: PropTypes.number,
  crossSellItemCheckOutClick: PropTypes.func,
  price: PropTypes.number,
  quantity: PropTypes.number,
  shippingCost: PropTypes.number,
};

Full.defaultProps = {
  checkoutBlocker: null,
  crossSellItemCheckOutClick: null,
  price: null,
  quantity: null,
  shippingCost: null,
};

Full.contextTypes = {
  zipCode: PropTypes.string,
  uploadedToAlbumPath: PropTypes.string,
};

export default Full;
